import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchBundleBySlug,
  getBundleSubscriptionForUser,
  createBundleSubscriptionCheckoutSession,
  createBundleOneTimePaymentIntent,
  buildSubscriptionCheckoutPayload,
  buildOneTimeCheckoutPayload,
  createBundleSubscriptionPortalSession
} from '@/services/bundleService';
import { Bundle, BundleSubscription, SupportedCurrency, BundleDefaultAddress } from '@/types/bundle';
import { formatDeliveryTime, formatProcessingTime, type ShippingTier as ShippingTierConfig } from '@/services/shippingService';
import LoadingSpinner from '@/components/LoadingSpinner';
import ScrollDownFAB from '@/components/ScrollDownFAB';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ShippingAddressModal from '@/components/ShippingAddressModal';
import AuthModalWrapper from '@/components/AuthModalWrapper';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useShippingAddress } from '@/context/ShippingAddressContext';
import { useAuthContext } from '@/lib/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/context/CurrencyContext';
import { useStripe as useStripeContext } from '@/context/StripeContext';
import { PaymentElement, useStripe as useStripeJs, useElements } from '@stripe/react-stripe-js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  ArrowLeft,
  Sparkles,
  ShoppingBag,
  Users,
  Truck,
  Package,
  CircleDollarSign,
  MapPin,
  CheckCircle2,
  Loader2,
  CreditCard,
  Clock
} from 'lucide-react';

type ShippingTierSummary = Partial<ShippingTierConfig> & {
  name?: string;
  description?: string;
};

type ShippingTierMap = Record<string, ShippingTierSummary>;

interface PendingPayment {
  orderId: string;
  amount: number;
  currency: string;
  bundleName: string;
  bundleSlug?: string;
  paymentIntentId: string;
}

const EXPRESS_SHIPPING_FALLBACK: ShippingTierConfig = {
  id: 'express',
  name: 'Express Shipping',
  description: 'Fastest arrival for urgent orders.',
  cost: 35,
  deliveryTimeMin: 3,
  deliveryTimeMax: 5,
  processingTimeMin: 1,
  processingTimeMax: 1,
  active: true,
  sortOrder: 2,
  maxWeight: 70
};

const BundleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const { shippingAddress, setShippingAddress, isAddressComplete } = useShippingAddress();
  const { selectedCurrency, formatDisplayAmount } = useCurrency();
  const { clientSecret, setClientSecret, setPaymentAmount } = useStripeContext();

  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'one_time' | 'subscription'>('one_time');
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [subscription, setSubscription] = useState<BundleSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [shippingTiers, setShippingTiers] = useState<ShippingTierMap>({});
  const [subscribing, setSubscribing] = useState(false);
  const [processingOneTime, setProcessingOneTime] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null);
  const [managingSub, setManagingSub] = useState(false);

  const usesAdminProvidedAddress = bundle?.addressPolicy === 'adminProvided' && !!bundle?.defaultAddress;
  const requiresCustomerAddress = !usesAdminProvidedAddress;

  type CheckoutAddressInput = {
    name: string;
    phone?: string;
    email?: string;
    address: BundleDefaultAddress['address'];
  };

  const buildCheckoutAddressPayload = (address: CheckoutAddressInput) => ({
    name: address.name,
    phone: address.phone,
    email: address.email || user?.email || undefined,
    address: address.address
  });

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setPendingPayment(null);
    setClientSecret(null);
    setPaymentAmount(null);
  };

  const handlePaymentSuccess = ({ orderId, paymentIntentId, paymentIntentClientSecret }: {
    orderId: string;
    paymentIntentId?: string;
    paymentIntentClientSecret?: string;
  }) => {
    handleClosePaymentModal();
    toast({
      title: 'Payment successful',
      description: 'We are getting your JamPack’d bundle ready!'
    });
    const params = new URLSearchParams({
      order_id: orderId,
      type: 'one_time'
    });

    if (paymentIntentId) {
      params.set('payment_intent', paymentIntentId);
    }

    if (paymentIntentClientSecret) {
      params.set('payment_intent_client_secret', paymentIntentClientSecret);
    }

    if (bundle?.slug) {
      params.set('bundle', bundle.slug);
    }

    navigate(`/bundle-order-confirmation?${params.toString()}`);
  };

  useEffect(() => {
    const loadBundle = async () => {
      try {
        setLoading(true);
        setError(null);
        const bundleData = await fetchBundleBySlug(slug || '');
        if (!bundleData || !bundleData.published || bundleData.disabled) {
          setError('Bundle not found or currently unavailable.');
          setBundle(null);
          return;
        }
        setBundle(bundleData);

        if (bundleData.purchaseTypes.subscription) {
          setSelectedType('subscription');
        } else if (bundleData.purchaseTypes.oneTime) {
          setSelectedType('one_time');
        }
      } catch (err) {
        console.error('Failed to load bundle:', err);
        setError('Unable to load bundle right now. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadBundle();
    }
  }, [slug]);

  useEffect(() => {
    const loadSubscription = async () => {
      if (!bundle || !user) {
        setSubscription(null);
        return;
      }

      try {
        setLoadingSubscription(true);
        const subscriptionData = await getBundleSubscriptionForUser(bundle.id, user.uid);
        setSubscription(subscriptionData);
      } catch (err) {
        console.error('Failed to fetch subscription info:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [bundle, user]);

  useEffect(() => {
    const loadShippingTiers = async () => {
      try {
        const configSnap = await getDoc(doc(db, 'shippingConfig', 'main'));
        if (configSnap.exists()) {
          const data = configSnap.data();
          const map: ShippingTierMap = {};
          (data.tiers || []).forEach((tier: any) => {
            map[tier.id] = {
              id: tier.id,
              name: tier.name,
              description: tier.description,
              cost: tier.cost,
              freeShippingThreshold: tier.freeShippingThreshold,
              deliveryTimeMin: tier.deliveryTimeMin,
              deliveryTimeMax: tier.deliveryTimeMax,
              processingTimeMin: tier.processingTimeMin,
              processingTimeMax: tier.processingTimeMax,
              active: tier.active,
              sortOrder: tier.sortOrder,
              maxWeight: tier.maxWeight,
              zones: tier.zones,
              excludedCountries: tier.excludedCountries,
              includedCountries: tier.includedCountries
            };
          });
          setShippingTiers(map);
        }
      } catch (err) {
        console.error('Failed to load shipping tiers:', err);
        setShippingTiers({});
      }
    };

    loadShippingTiers();
  }, []);

  const currencyKey = selectedCurrency.toLowerCase() as SupportedCurrency;

  const activePrice = useMemo(() => {
    if (!bundle) return null;
    const prices = bundle.prices?.[currencyKey];
    if (!prices) return null;
    return selectedType === 'subscription' ? prices.subscription : prices.oneTime;
  }, [bundle, currencyKey, selectedType]);

  // Consider any non-canceled subscription as subscribed to surface management quickly
  const isSubscribed = !!subscription && subscription.status !== 'canceled';
  const subscriptionHasStripeId = !!subscription?.stripeSubscriptionId;

  const computeNextBillingDate = useCallback((day: number) => {
    const now = new Date();
    const target = new Date(now.getTime());

    if (now.getDate() >= day) {
      target.setMonth(target.getMonth() + 1);
    }

    const maxDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(day, maxDay));
    target.setHours(0, 0, 0, 0);
    return target;
  }, []);

  const upcomingBillingDate = useMemo(() => {
    if (subscription?.nextBillingAt) {
      const value = subscription.nextBillingAt instanceof Date
        ? subscription.nextBillingAt
        : new Date(subscription.nextBillingAt);
      if (!Number.isNaN(value.getTime())) {
        return value;
      }
    }
    return null;
  }, [subscription?.nextBillingAt]);

  const handleManageSubscription = async () => {
    if (!bundle || !user || !subscriptionHasStripeId) return;
    try {
      setManagingSub(true);
      const { url } = await createBundleSubscriptionPortalSession({
        bundleId: bundle.id,
        userId: user.uid,
        returnUrl: window.location.href
      });
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Failed to create billing portal session:', err);
      toast({
        title: 'Unable to open portal',
        description: 'Please try again shortly.',
        variant: 'destructive'
      });
    } finally {
      setManagingSub(false);
    }
  };

  const ensureAddressBeforeCheckout = () => {
    if (usesAdminProvidedAddress) {
      return true;
    }
    if (isAddressComplete && shippingAddress?.value) {
      return true;
    }
    setAddressModalOpen(true);
    toast({
      title: 'Address Required',
      description: 'Please confirm your delivery address before continuing.',
      variant: 'destructive'
    });
    return false;
  };

  const handleSubscriptionCheckout = async () => {
    if (!bundle || !bundle.purchaseTypes.subscription) return;

    if (!user) {
      setAuthModalOpen(true);
      return;
    }

    if (bundle.addressPolicy === 'adminProvided' && !bundle.defaultAddress) {
      toast({
        title: 'Shipping configuration missing',
        description: 'This bundle uses a preset address, but none has been configured yet. Please contact support.',
        variant: 'destructive'
      });
      return;
    }

    if (!ensureAddressBeforeCheckout()) {
      return;
    }

    if (!bundle.shippingConfig?.subscriptionTierId) {
      toast({
        title: 'Shipping configuration missing',
        description: 'This bundle is not yet ready for subscription checkout. Please try again soon.',
        variant: 'destructive'
      });
      return;
    }

    if (!activePrice) {
      toast({
        title: 'Pricing unavailable',
        description: 'Subscription pricing is not configured for this currency yet.',
        variant: 'destructive'
      });
      return;
    }

    if (!user.email) {
      toast({
        title: 'Email required',
        description: 'Please add an email address to your profile before subscribing.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubscribing(true);
      const address = usesAdminProvidedAddress ? bundle.defaultAddress : shippingAddress?.value;
      if (!address) {
        throw new Error('Shipping address not available');
      }
      const payload = buildSubscriptionCheckoutPayload({
        bundleId: bundle.id,
        currency: currencyKey,
        userId: user.uid,
        userEmail: user.email || '',
        successUrl: `${window.location.origin}/bundle-order-confirmation?bundle=${encodeURIComponent(bundle.slug)}&type=subscription&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href,
        shippingAddress: buildCheckoutAddressPayload(address),
        shippingTierId: bundle.shippingConfig.subscriptionTierId ?? null
      });
      sessionStorage.setItem('payment-flow-initiated', JSON.stringify({
        timestamp: Date.now(),
        orderId: '',
        userId: user.uid,
        flow: 'bundle-subscription'
      }));
      const response = await createBundleSubscriptionCheckoutSession(payload);

      if (response?.url) {
        window.location.href = response.url;
      } else {
        throw new Error('Checkout session did not return a URL');
      }
    } catch (err) {
      console.error('Subscription checkout failed:', err);
      toast({
        title: 'Checkout failed',
        description: err instanceof Error ? err.message : 'Unable to start subscription checkout.',
        variant: 'destructive'
      });
    } finally {
      setSubscribing(false);
    }
  };

  const handleOneTimeCheckout = async () => {
    if (!bundle || !bundle.purchaseTypes.oneTime) return;

    if (!user) {
      setAuthModalOpen(true);
      return;
    }

    if (bundle.addressPolicy === 'adminProvided' && !bundle.defaultAddress) {
      toast({
        title: 'Shipping configuration missing',
        description: 'This bundle uses a preset address, but none has been configured yet. Please contact support.',
        variant: 'destructive'
      });
      return;
    }

    if (!ensureAddressBeforeCheckout()) {
      return;
    }

    if (!bundle.shippingConfig?.oneTimeTierId) {
      toast({
        title: 'Shipping configuration missing',
        description: 'One-time bundle checkout will be available soon. Please check back later.',
        variant: 'destructive'
      });
      return;
    }

    if (!activePrice) {
      toast({
        title: 'Pricing unavailable',
        description: 'Pricing for the selected currency is not configured yet.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setProcessingOneTime(true);
      const checkoutAddressSource: CheckoutAddressInput | undefined = usesAdminProvidedAddress
        ? bundle.defaultAddress || undefined
        : shippingAddress?.value;

      if (!checkoutAddressSource) {
        throw new Error('Shipping address not available');
      }

      const formattedShippingAddress = buildCheckoutAddressPayload(checkoutAddressSource);
      const payload = buildOneTimeCheckoutPayload({
        bundleId: bundle.id,
        currency: currencyKey,
        userId: user.uid,
        userEmail: user.email || undefined,
        shippingAddress: formattedShippingAddress,
        shippingTierId: bundle.shippingConfig.oneTimeTierId ?? null
      });

      const response = await createBundleOneTimePaymentIntent(payload);
      console.log('[bundle:one-time] createBundleOneTimePaymentIntent response', response);

      if (!response.clientSecret) {
        throw new Error('Payment could not be initialized.');
      }

      const paymentIntentId = response.paymentIntentId || response.clientSecret.split('_secret_')[0];

      setClientSecret(response.clientSecret);
      setPaymentAmount(Math.round(response.total * 100));
      setPendingPayment({
        orderId: response.orderId,
        amount: response.total,
        currency: response.currency || currencyKey,
        bundleName: bundle.name,
        bundleSlug: bundle.slug,
        paymentIntentId
      });

      localStorage.setItem('pendingOrderUpdate', JSON.stringify({
        orderId: response.orderId,
        customer: {
          name: checkoutAddressSource.name,
          phone: checkoutAddressSource.phone,
          email: user.email || checkoutAddressSource.email || '',
          address: checkoutAddressSource.address
        },
        userId: user.uid,
        timestamp: Date.now()
      }));

      sessionStorage.setItem('payment-flow-initiated', JSON.stringify({
        timestamp: Date.now(),
        orderId: response.orderId,
        userId: user.uid,
        paymentIntentId
      }));

      setPaymentModalOpen(true);
    } catch (err) {
      console.error('One-time checkout failed:', err);
      toast({
        title: 'Checkout unavailable',
        description: err instanceof Error ? err.message : 'Unable to start one-time checkout.',
        variant: 'destructive'
      });
      setClientSecret(null);
      setPaymentAmount(null);
    } finally {
      setProcessingOneTime(false);
    }
  };

  const shippingTierLabel = (tierId?: string | null) => {
    if (!tierId) return 'Not configured yet';
    return shippingTiers[tierId]?.name || tierId;
  };

  const expressShippingInfo = useMemo(() => {
    const expressTier = shippingTiers['express'];
    const mergedTier: ShippingTierConfig = {
      ...EXPRESS_SHIPPING_FALLBACK,
      ...expressTier,
      id: 'express',
      name: expressTier?.name || EXPRESS_SHIPPING_FALLBACK.name,
      description: expressTier?.description || EXPRESS_SHIPPING_FALLBACK.description,
      deliveryTimeMin: expressTier?.deliveryTimeMin ?? EXPRESS_SHIPPING_FALLBACK.deliveryTimeMin,
      deliveryTimeMax: expressTier?.deliveryTimeMax ?? EXPRESS_SHIPPING_FALLBACK.deliveryTimeMax,
      processingTimeMin: expressTier?.processingTimeMin ?? EXPRESS_SHIPPING_FALLBACK.processingTimeMin,
      processingTimeMax: expressTier?.processingTimeMax ?? EXPRESS_SHIPPING_FALLBACK.processingTimeMax,
      cost: expressTier?.cost ?? EXPRESS_SHIPPING_FALLBACK.cost,
      active: expressTier?.active ?? EXPRESS_SHIPPING_FALLBACK.active,
      sortOrder: expressTier?.sortOrder ?? EXPRESS_SHIPPING_FALLBACK.sortOrder,
      freeShippingThreshold: expressTier?.freeShippingThreshold ?? EXPRESS_SHIPPING_FALLBACK.freeShippingThreshold,
      maxWeight: expressTier?.maxWeight ?? EXPRESS_SHIPPING_FALLBACK.maxWeight,
      zones: expressTier?.zones ?? EXPRESS_SHIPPING_FALLBACK.zones,
      excludedCountries: expressTier?.excludedCountries ?? EXPRESS_SHIPPING_FALLBACK.excludedCountries,
      includedCountries: expressTier?.includedCountries ?? EXPRESS_SHIPPING_FALLBACK.includedCountries
    };

    return {
      name: mergedTier.name,
      description: mergedTier.description,
      processingLabel: formatProcessingTime(mergedTier),
      deliveryLabel: formatDeliveryTime(mergedTier)
    };
  }, [shippingTiers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-white to-gray-50 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Bundle Not Available</h1>
        <p className="text-gray-600 max-w-md">{error || 'This bundle could not be found or is currently unavailable.'}</p>
        <Button onClick={() => navigate('/bundles')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bundles
        </Button>
      </div>
    );
  }

  const priceLabel = activePrice != null ? formatDisplayAmount(activePrice) : 'Price unavailable';
  const totalBundleItems = (bundle.items ?? []).reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

        <div className="grid gap-10 lg:grid-cols-[1.2fr,1fr]">
          <div>
            <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
              <div className="group relative w-full overflow-hidden bg-gray-100 aspect-[16/9]">
                <img
                  src={bundle.heroImage || bundle.images?.[0] || '/api/placeholder/960/540'}
                  alt={bundle.name}
                  className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                />
              </div>

              {bundle.images && bundle.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2 p-4">
                  {bundle.images.slice(1, 5).map((image, index) => (
                    <div key={`${image}-${index}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-gray-100">
                      <img src={image} alt={`${bundle.name} view ${index + 2}`} className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <section className="mt-8 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Package className="h-5 w-5 text-jamaica-green" /> What's Inside
                </h2>
                <Badge variant="outline" className="border-jamaica-green text-jamaica-green">
                  {totalBundleItems} total items
                </Badge>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {bundle.description
                  ? bundle.description
                  : "Each bundle is carefully curated with JamPack'd favourites. Quantities listed reflect what you'll receive with every order."}
              </p>
              <ul className="mt-6 space-y-3">
                {bundle.items.map((item) => (
                  <li
                    key={item.productId}
                    className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
                        {item.image ? (
                          <img src={item.image} alt={item.productName || 'Item'} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-gray-100 text-gray-400">
                            <Package className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-gray-900">{item.productName || 'Product'}</p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          {item.brand && (
                            <span className="font-semibold text-gray-700 uppercase tracking-wide">{item.brand}</span>
                          )}
                          {item.brand && (item.netWeight || item.weightUnit) && (
                            <span className="text-gray-300">•</span>
                          )}
                          {(item.netWeight || item.weightUnit) && (
                            <span>
                              {item.netWeight ?? '?'}
                              {item.weightUnit ? item.weightUnit.toLowerCase() : ''}
                            </span>
                          )}
                        </div>
                        {item.customDescription && (
                          <p className="text-xs text-gray-600 max-w-xs">
                            {item.customDescription}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="border-jamaica-green text-jamaica-green">
                      x{item.quantity}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="space-y-6" id="bundle-purchase-card">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="h-6 w-6 text-jamaica-green" /> {bundle.name}
                </h1>
                <div className="space-y-1 text-left sm:text-right">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Starting at</p>
                  <p className="text-2xl font-bold text-gray-900">{priceLabel}</p>
                  <div className="flex flex-col items-start sm:items-end gap-1">
                    <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1 uppercase tracking-wide bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full whitespace-nowrap">
                      <Truck className="h-3 w-3" /> Shipping Included
                    </span>
                    {selectedType === 'subscription' && (
                      <p className="text-xs text-gray-500">charges now, renews monthly</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  variant={selectedType === 'one_time' ? 'default' : 'outline'}
                  disabled={!bundle.purchaseTypes.oneTime}
                  onClick={() => setSelectedType('one_time')}
                  className="justify-center"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" /> One-Time
                </Button>
                <Button
                  variant={selectedType === 'subscription' ? 'default' : 'outline'}
                  disabled={!bundle.purchaseTypes.subscription}
                  onClick={() => setSelectedType('subscription')}
                  className="justify-center"
                >
                  <Users className="h-4 w-4 mr-2" /> Subscription
                </Button>
              </div>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 text-jamaica-green mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {expressShippingInfo.name}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-start gap-3 rounded-xl border border-white bg-white px-3 py-2 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
                      <Clock className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Processing</p>
                        <p className="text-sm font-semibold text-gray-800">{expressShippingInfo.processingLabel}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-xl border border-white bg-white px-3 py-2 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
                      <Truck className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Delivery</p>
                        <p className="text-sm font-semibold text-gray-800">{expressShippingInfo.deliveryLabel}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {requiresCustomerAddress && (
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-jamaica-green" /> Delivery Address
                      </p>
                      <Button variant="outline" size="sm" onClick={() => setAddressModalOpen(true)}>
                        {isAddressComplete ? 'Change' : 'Add Address'}
                      </Button>
                    </div>

                    {isAddressComplete && shippingAddress?.value ? (
                      <div className="mt-3 text-sm text-gray-700">
                        <p className="font-semibold">{shippingAddress.value.name}</p>
                        <p>{shippingAddress.value.address.line1}</p>
                        {shippingAddress.value.address.line2 && <p>{shippingAddress.value.address.line2}</p>}
                        <p>
                          {shippingAddress.value.address.city}, {shippingAddress.value.address.state}{' '}
                          {shippingAddress.value.address.postal_code}
                        </p>
                        <p>{shippingAddress.value.address.country}</p>
                        {shippingAddress.value.phone && <p className="mt-1 text-xs text-gray-500">{shippingAddress.value.phone}</p>}
                      </div>
                    ) : (
                      <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-500">
                        No address selected yet. Tap “Add Address” to confirm where this bundle should be delivered.
                      </div>
                    )}
                  </div>
                )}

                {/* Shipping Configuration and Current Stats cards removed */}

                <div className="space-y-3">
                  {selectedType === 'subscription' ? (
                    <Button
                      className="w-full h-12 text-base"
                      onClick={handleSubscriptionCheckout}
                      disabled={subscribing || !bundle.purchaseTypes.subscription || isSubscribed}
                    >
                      {subscribing ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" /> Starting Checkout...
                        </>
                      ) : isSubscribed ? (
                        <>
                          <CheckCircle2 className="h-5 w-5 mr-2" /> Already Subscribed
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-5 w-5 mr-2" /> Subscribe &amp; Checkout
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      className="w-full h-12 text-base"
                      variant="secondary"
                      onClick={handleOneTimeCheckout}
                      disabled={processingOneTime || !bundle.purchaseTypes.oneTime}
                    >
                      {processingOneTime ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" /> Creating Order...
                        </>
                      ) : (
                        <>
                          <CircleDollarSign className="h-5 w-5 mr-2" /> One-Time Purchase
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {isSubscribed && selectedType === 'subscription' && subscriptionHasStripeId && (
                  <Button
                    className="w-full h-12 text-base"
                    variant="secondary"
                    onClick={handleManageSubscription}
                    disabled={managingSub}
                  >
                    {managingSub ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Opening...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5 mr-2" /> Manage Subscription
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ScrollDownFAB
        className="sm:hidden !bottom-24"
        scrollTarget="#bundle-purchase-card"
        tooltipText="Scroll to purchase"
      />

      {requiresCustomerAddress && (
        <ShippingAddressModal
          open={addressModalOpen}
          onOpenChange={setAddressModalOpen}
          onAddressConfirmed={(details) => {
            setShippingAddress(details);
            setAddressModalOpen(false);
          }}
          currentAddress={shippingAddress}
        />
      )}

      <AuthModalWrapper isOpen={authModalOpen} onOpenChange={setAuthModalOpen} />
      <BundlePaymentDialog
        open={paymentModalOpen && !!pendingPayment}
        onOpenChange={(open) => {
          if (open) {
            setPaymentModalOpen(true);
          } else {
            handleClosePaymentModal();
          }
        }}
        clientSecret={clientSecret}
        pendingPayment={pendingPayment}
        onDismiss={handleClosePaymentModal}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BundleDetail;

type BundlePaymentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientSecret: string | null;
  pendingPayment: PendingPayment | null;
  onDismiss: () => void;
  onPaymentSuccess: (payload: { orderId: string; paymentIntentId?: string; paymentIntentClientSecret?: string }) => void;
};

const BundlePaymentDialog: React.FC<BundlePaymentDialogProps> = ({
  open,
  onOpenChange,
  clientSecret,
  pendingPayment,
  onDismiss,
  onPaymentSuccess
}) => {
  const stripe = useStripeJs();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setIsConfirming(false);
      setError(null);
    }
  }, [open]);

  const formattedAmount = useMemo(() => {
    if (!pendingPayment) return '';
    const currency = (pendingPayment.currency || 'usd').toUpperCase();
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
      }).format(pendingPayment.amount);
    } catch (_) {
      return `${pendingPayment.amount.toFixed(2)} ${currency}`;
    }
  }, [pendingPayment]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isConfirming) {
      return;
    }

    if (nextOpen) {
      onOpenChange(true);
    } else {
      setError(null);
      onDismiss();
    }
  };

  const handleConfirmPayment = async () => {
    if (!stripe || !elements || !clientSecret || !pendingPayment) {
      return;
    }

    setIsConfirming(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Please check your payment details and try again.');
        setIsConfirming(false);
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/bundle-order-confirmation?order_id=${pendingPayment.orderId}&type=one_time${pendingPayment.bundleSlug ? `&bundle=${encodeURIComponent(pendingPayment.bundleSlug)}` : ''}`
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment could not be completed. Please try again.');
        setIsConfirming(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        setIsConfirming(false);
        onPaymentSuccess({
          orderId: pendingPayment.orderId,
          paymentIntentId: paymentIntent?.id,
          paymentIntentClientSecret: paymentIntent?.client_secret || undefined
        });
      } else {
        setIsConfirming(false);
        // For statuses like requires_action, Stripe will handle redirects. As a fallback, navigate to confirmation.
        onPaymentSuccess({ orderId: pendingPayment.orderId });
      }
    } catch (err) {
      console.error('Bundle payment confirmation failed:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="flex w-[calc(100vw-1.5rem)] flex-col overflow-hidden p-0 max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-4rem)] sm:max-w-lg">
        <div className="px-4 pt-4 pb-2 sm:px-6">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold sm:text-lg">
              <Sparkles className="h-5 w-5 text-jamaica-green" /> Complete Your Bundle Purchase
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Enter your payment details to confirm this one-time order. Your bundle will be prepared right away after payment succeeds.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">
          {pendingPayment ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-900">Bundle:</span> {pendingPayment.bundleName}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-900">Total:</span> {formattedAmount}
                </p>
              </div>

              {!clientSecret ? (
                <div className="flex items-center justify-center rounded-lg bg-gray-50 py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
                  <span className="ml-2 text-sm text-gray-600">Preparing secure checkout...</span>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <PaymentElement options={{ layout: 'tabs' }} />
                </div>
              )}

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <div className="py-6 text-sm text-gray-500">Payment details not available.</div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:justify-end sm:gap-2 sm:px-6">
          <Button variant="outline" onClick={onDismiss} disabled={isConfirming} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPayment}
            disabled={isConfirming || !clientSecret || !pendingPayment}
            className="w-full sm:w-auto"
          >
            {isConfirming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...
              </>
            ) : (
              'Pay Now'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
